import axios from "axios";
import StatsPreview from "../interfaces/StatsPreview";
import { BACKEND } from "../resources/constants/urls";
import {
  endPreviewSync,
  getPreviewMirror,
  mergePreviewMirror,
  notifyPreviewMirrorSync,
  startPreviewSync,
  updatePreviewMirror,
} from "@appsinti/preview-mirror";
import auth from "../resources/api/auth";
import CurrentTemperature from "@appsinti/common-utils/interfaces/weather/CurrentTemperature";
import ForecastTemperature from "@appsinti/common-utils/interfaces/weather/ForecastTemperature";
import IGroup from "@appsinti/common-utils/interfaces/IGroup";

const { Authorization } = auth;

const PREVIEW_TIMEOUT = 20000;

type WeatherData = { [key: string]: CurrentTemperature | ForecastTemperature };

export const statsSummarySyncKey = (
  appId: number,
  groupId: number | string,
): string => `${appId}:${groupId}`;

const fetchGroupWeather = async (
  appId: number,
  groupId: number | string,
  signal?: AbortSignal,
): Promise<WeatherData | undefined> => {
  try {
    const { data } = await axios.get<WeatherData>(
      `${BACKEND}/appsinti/stats-summary/weather/preview`,
      {
        headers: { "app-id": appId, "group-id": groupId, Authorization },
        signal,
        timeout: PREVIEW_TIMEOUT,
      },
    );
    return data;
  } catch (error) {
    if (!axios.isCancel(error)) console.error(error);
    return undefined;
  }
};

const fetchAndStoreGroupPreview = async (
  appId: number,
  groupId: number | string,
  signal?: AbortSignal,
): Promise<void> => {
  const syncKey = statsSummarySyncKey(appId, groupId);
  startPreviewSync(syncKey);

  try {
    const [current, weatherData] = await Promise.all([
      axios.get<StatsPreview>(
        `${BACKEND}/appsinti/stats-summary/data/preview`,
        {
          headers: { "app-id": appId, "group-id": groupId, Authorization },
          signal,
          timeout: PREVIEW_TIMEOUT,
        },
      ),
      fetchGroupWeather(appId, groupId, signal),
    ]);

    const { data } = current;
    data.weatherData = weatherData;
    data.lastUpdate = new Date().getTime();

    mergePreviewMirror({ [groupId]: data }, appId);
    notifyPreviewMirrorSync();
  } catch (error) {
    if (!axios.isCancel(error)) console.error(error);
  } finally {
    endPreviewSync(syncKey);
  }
};

const pruneRemovedGroups = (appId: number, groupIds: string[]): void => {
  const mirror = getPreviewMirror(appId);
  if (!mirror) return;

  let stored: { [key: string]: StatsPreview };
  try {
    stored = JSON.parse(mirror) as { [key: string]: StatsPreview };
  } catch {
    return;
  }

  const kept: { [key: string]: StatsPreview } = {};
  for (const groupId of groupIds) {
    if (stored[groupId] !== undefined) kept[groupId] = stored[groupId];
  }

  if (Object.keys(kept).length === Object.keys(stored).length) return;

  updatePreviewMirror(JSON.stringify(kept), appId);
  notifyPreviewMirrorSync();
};

export const fetchAndStoreStatsSummaryPreview = async (
  appId: number,
  groups: { [key: number]: IGroup },
  signal?: AbortSignal,
): Promise<void> => {
  if (!appId) return;

  const groupIds = Object.keys(groups);

  await Promise.all(
    groupIds.map((groupId) =>
      fetchAndStoreGroupPreview(appId, groupId, signal),
    ),
  );

  if (signal?.aborted) return;
  pruneRemovedGroups(appId, groupIds);
};
